import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import MultiSelect from "components/forms/MultiSelect";

const deepClone = (obj) => JSON.parse(JSON.stringify(obj == null ? {} : obj));

const toQuestionLangFieldKey = (langCode) => {
  const upper = String(langCode || "").toUpperCase();
  if (upper === "EN") return "question";
  return upper.toLowerCase();
};

const toOutcomeNameFieldKey = (langCode) => {
  const upper = String(langCode || "").toUpperCase();
  if (upper === "EN") return "name";
  return `${upper.toLowerCase()}_name`;
};

const collectExistingLangsFromOutcomes = (outcomes) => {
  const langs = new Set();
  (Array.isArray(outcomes) ? outcomes : []).forEach((out) => {
    Object.keys(out || {}).forEach((k) => {
      if (/_name$/.test(k)) {
        const code = k.replace(/_name$/, "");
        if (code && code.length <= 3) langs.add(code.toUpperCase());
      }
    });
    (Array.isArray(out?.questions) ? out.questions : []).forEach((q) => {
      Object.keys(q || {}).forEach((k) => {
        if (k.length <= 3 && k !== "q" && k !== "s" && k !== "id") {
          langs.add(k.toUpperCase());
        }
      });
    });
  });
  return Array.from(langs);
};

const ensureRenumbering = (outcomes) => {
  const next = Array.isArray(outcomes) ? outcomes : [];
  next.forEach((outcome, oIdx) => {
    outcome.id = oIdx + 1;
    const questions = Array.isArray(outcome.questions) ? outcome.questions : [];
    questions.forEach((q, sIdx) => {
      q.q = outcome.id;
      q.s = sIdx + 1;
      q.id = String(sIdx + 1);
      if (!Array.isArray(q.scale) || q.scale.length < 2) {
        q.scale = ["Strongly Disagree", "Strongly Agree"];
      }
      if (typeof q.allowSkip !== "boolean") q.allowSkip = true;
      if (typeof q.useScale !== "boolean") q.useScale = false;
    });
    outcome.questions = questions;
  });
  return next;
};

const LANGUAGE_OPTIONS = [
  { key: "FR", value: "FR", text: "French (FR)" },
  { key: "POR", value: "POR", text: "Portuguese (POR)" },
  { key: "ES", value: "ES", text: "Spanish (ES)" },
];

export default function OutcomeStructureManagerModal({ open, onClose, outcomes, onSave, languages }) {
  const [working, setWorking] = useState([]);
  const [selectedOutcomeIndex, setSelectedOutcomeIndex] = useState(0);
  const [selectedLangs, setSelectedLangs] = useState([]);

  useEffect(() => {
    if (open) {
      const base = Array.isArray(outcomes) ? deepClone(outcomes) : [];
      const normalized = ensureRenumbering(base);
      setWorking(normalized);
      setSelectedOutcomeIndex(0);
      const fromProps = Array.isArray(languages)
        ? languages.map((l) => String(l).toUpperCase()).filter((l) => l !== "EN")
        : [];
      const fromData = collectExistingLangsFromOutcomes(normalized).filter((l) => l !== "EN");
      const merged = Array.from(new Set([...(fromProps || []), ...(fromData || [])]));
      setSelectedLangs(merged);
    }
  }, [open, outcomes, languages]);

  const effectiveLanguages = useMemo(() => {
    const fromData = collectExistingLangsFromOutcomes(working);
    const merged = new Set(["EN", ...(selectedLangs || []), ...fromData]);
    return Array.from(merged);
  }, [selectedLangs, working]);

  const languageFieldDefs = useMemo(() => {
    return effectiveLanguages.map((LC) => {
      const upper = String(LC).toUpperCase();
      const lower = upper.toLowerCase();
      return {
        code: upper,
        qField: toQuestionLangFieldKey(upper),
        nameField: toOutcomeNameFieldKey(upper),
        scaleField: upper === "EN" ? "scale" : `${lower}_scale`,
      };
    });
  }, [effectiveLanguages]);

  const currentOutcome = working?.[selectedOutcomeIndex] || null;
  const currentQuestions = Array.isArray(currentOutcome?.questions) ? currentOutcome.questions : [];

  const addOutcome = () => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const newOutcome = { id: next.length + 1, name: "", questions: [], use_custom_header: false };
      languageFieldDefs.forEach(({ nameField }) => {
        if (nameField !== "name") newOutcome[nameField] = "";
      });
      next.push(newOutcome);
      return ensureRenumbering(next);
    });
    setSelectedOutcomeIndex(working.length);
  };

  const removeOutcome = (index) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      next.splice(index, 1);
      return ensureRenumbering(next);
    });
    setSelectedOutcomeIndex(0);
  };

  const setOutcomeField = (oIndex, field, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const out = { ...(next[oIndex] || {}) };
      out[field] = value;
      next[oIndex] = out;
      return next;
    });
  };

  const addQuestion = (oIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const out = { ...(next[oIndex] || {}) };
      const arr = Array.isArray(out.questions) ? [...out.questions] : [];
      const q = {
        question: "",
        allowSkip: true,
        useScale: false,
        scale: ["Strongly Disagree", "Strongly Agree"],
      };
      languageFieldDefs.forEach(({ qField, scaleField }) => {
        if (qField !== "question") q[qField] = "";
        if (scaleField && scaleField !== "scale" && !Array.isArray(q[scaleField])) {
          q[scaleField] = ["", ""];
        }
      });
      arr.push(q);
      out.questions = arr;
      next[oIndex] = out;
      return ensureRenumbering(next);
    });
  };

  const removeQuestion = (oIndex, qIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const out = { ...(next[oIndex] || {}) };
      const arr = Array.isArray(out.questions) ? [...out.questions] : [];
      arr.splice(qIndex, 1);
      out.questions = arr;
      next[oIndex] = out;
      return ensureRenumbering(next);
    });
  };

  const setQuestionField = (oIndex, qIndex, field, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const out = { ...(next[oIndex] || {}) };
      const arr = Array.isArray(out.questions) ? [...out.questions] : [];
      const q = { ...(arr[qIndex] || {}) };
      q[field] = value;
      arr[qIndex] = q;
      out.questions = arr;
      next[oIndex] = out;
      return next;
    });
  };

  const handleSave = () => {
    const data = ensureRenumbering(deepClone(working));
    onSave && onSave({ outcomes: data, languages: selectedLangs });
    onClose && onClose();
  };

  const handleDialogChange = (nextOpen) => {
    if (!nextOpen) onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="flex max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 px-6 pt-6">
          <DialogTitle>Manage Outcome Structure</DialogTitle>
          <DialogDescription>
            Add or remove outcomes and questions, and manage multilingual text.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto px-6 pb-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Languages</CardTitle>
            </CardHeader>
            <CardContent>
              <MultiSelect
                options={LANGUAGE_OPTIONS}
                value={selectedLangs}
                onChange={(value) =>
                  setSelectedLangs(Array.isArray(value) ? value.map((v) => String(v).toUpperCase()).filter((v) => v !== "EN") : [])
                }
                placeholder="Select languages"
              />
              <p className="mt-2 text-xs text-muted-foreground">English (EN) is always included.</p>
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Outcomes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {(working || []).map((out, oi) => (
                    <div key={`out-${oi}`} className="space-y-2 rounded-lg border border-border/60 bg-card/60 p-3">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedOutcomeIndex === oi ? "default" : "outline"}
                          onClick={() => setSelectedOutcomeIndex(oi)}
                          className="shrink-0"
                        >
                          Outcome {oi + 1}
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeOutcome(oi)}
                          className="ml-auto text-destructive"
                          aria-label="Remove outcome"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        placeholder="Outcome name (EN)"
                        value={out?.name || ""}
                        onChange={(e) => setOutcomeField(oi, "name", e.target.value)}
                      />
                      {languageFieldDefs
                        .filter(({ code }) => code !== "EN")
                        .map(({ code, nameField }) => (
                          <Input
                            key={`oname-${code}`}
                            placeholder={`Outcome name (${code})`}
                            value={out?.[nameField] || ""}
                            onChange={(e) => setOutcomeField(oi, nameField, e.target.value)}
                          />
                        ))}
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={!!out?.use_custom_header}
                          onCheckedChange={(checked) =>
                            setOutcomeField(oi, "use_custom_header", !!checked)
                          }
                        />
                        <label className="text-sm font-medium text-foreground">
                          Use custom header
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="secondary" size="sm" onClick={addOutcome} className="inline-flex items-center gap-2">
                  <Plus className="h-4 w-4" /> Add outcome
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Questions</CardTitle>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => addQuestion(selectedOutcomeIndex)}
                    className="inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" /> Add question
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {(currentQuestions || []).length === 0 && (
                  <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
                    Select an outcome and add your first question.
                  </div>
                )}
                {(currentQuestions || []).map((q, qi) => (
                  <div key={`q-${qi}`} className="space-y-4 rounded-lg border border-border/60 bg-card/40 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-foreground">
                        Question {qi + 1} {Number.isFinite(Number(q?.s)) ? `(s ${q.s})` : ""}
                      </p>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeQuestion(selectedOutcomeIndex, qi)}
                        className="text-destructive"
                        aria-label="Remove question"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      {languageFieldDefs.map(({ code, qField }) => (
                        <label key={`qlang-${code}`} className="flex flex-col gap-2 text-sm font-medium text-foreground">
                          <span>{code === "EN" ? "English" : code}</span>
                          <Textarea
                            placeholder={code === "EN" ? "Question (EN)" : `Translation (${code})`}
                            rows={code === "EN" ? 4 : 3}
                            value={(q && (q[qField] ?? ""))}
                            onChange={(e) => setQuestionField(selectedOutcomeIndex, qi, qField, e.target.value)}
                            className="min-h-[120px]"
                          />
                        </label>
                      ))}
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
                        <span>Scale (min label)</span>
                        <Input
                          value={Array.isArray(q?.scale) && q.scale.length > 0 ? q.scale[0] : ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const nextScale = Array.isArray(q?.scale) ? [...q.scale] : ["", ""];
                            nextScale[0] = v;
                            setQuestionField(selectedOutcomeIndex, qi, "scale", nextScale);
                          }}
                        />
                      </label>
                      <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
                        <span>Scale (max label)</span>
                        <Input
                          value={Array.isArray(q?.scale) && q.scale.length > 1 ? q.scale[1] : ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const nextScale = Array.isArray(q?.scale) ? [...q.scale] : ["", ""];
                            if (!nextScale[0]) nextScale[0] = "Strongly Disagree";
                            nextScale[1] = v;
                            setQuestionField(selectedOutcomeIndex, qi, "scale", nextScale);
                          }}
                        />
                      </label>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <Checkbox
                            checked={!!q?.allowSkip}
                            onCheckedChange={(checked) =>
                              setQuestionField(selectedOutcomeIndex, qi, "allowSkip", !!checked)
                            }
                          />
                          Allow skip
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <Checkbox
                            checked={!!q?.useScale}
                            onCheckedChange={(checked) =>
                              setQuestionField(selectedOutcomeIndex, qi, "useScale", !!checked)
                            }
                          />
                          Use scale
                        </label>
                      </div>
                    </div>

                    {languageFieldDefs
                      .filter(({ code }) => code !== "EN")
                      .map(({ code, scaleField }) => (
                        <div key={`scale-${code}`} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                          <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
                            <span>{`Scale min (${code})`}</span>
                            <Input
                              value={Array.isArray(q?.[scaleField]) && q[scaleField].length > 0 ? q[scaleField][0] : ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                const nextScale = Array.isArray(q?.[scaleField]) ? [...q[scaleField]] : ["", ""];
                                nextScale[0] = v;
                                setQuestionField(selectedOutcomeIndex, qi, scaleField, nextScale);
                              }}
                            />
                          </label>
                          <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
                            <span>{`Scale max (${code})`}</span>
                            <Input
                              value={Array.isArray(q?.[scaleField]) && q[scaleField].length > 1 ? q[scaleField][1] : ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                const nextScale = Array.isArray(q?.[scaleField]) ? [...q[scaleField]] : ["", ""];
                                if (!nextScale[0]) nextScale[0] = q?.scale?.[0] || "Strongly Disagree";
                                nextScale[1] = v;
                                setQuestionField(selectedOutcomeIndex, qi, scaleField, nextScale);
                              }}
                            />
                          </label>
                        </div>
                      ))}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
        <DialogFooter className="shrink-0 border-t border-border/60 bg-muted/10 px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
